import { Popover, useTheme } from '@mui/material';
import React from 'react';

import { AddFilterConfigure } from './AddFilterConfigure';
import { FilterFieldDef } from './AddFilterModal.types';
import { AdvancedFilter } from './FilterBar.props';

export interface EditFilterPopoverProps {
  /** Chip element the popover anchors to. When null, the popover stays closed. */
  anchorEl: HTMLElement | null;
  /** Field definition for the filter being edited. When null (the filter's
   *  field is not in the catalog), the popover stays closed. */
  field: FilterFieldDef | null;
  /** The active filter whose values seed the form. */
  filter: AdvancedFilter | null;
  /** Option source for `type: 'list'` fields. */
  options?: Array<{ label: string; value: string }>;
  /** Fired with the updated filter when the user applies the inline edit. */
  onApply: (filter: AdvancedFilter) => void;
  /** Dismiss without saving (close button or click-away). */
  onClose: () => void;
}

/**
 * Lightweight inline editor for an already-applied filter chip. Renders the
 * shared {@link AddFilterConfigure} step inside a small popover anchored to the
 * chip, so a quick tweak no longer launches the full-screen add-filter modal.
 * Adding a brand-new filter still goes through the modal.
 */
export const EditFilterPopover: React.FC<EditFilterPopoverProps> = ({
  anchorEl,
  field,
  filter,
  options,
  onApply,
  onClose,
}) => {
  const theme = useTheme();
  const open = Boolean(anchorEl) && Boolean(field) && Boolean(filter);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            width: 320,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.v4.surface,
            border: `1px solid ${theme.palette.v4.border}`,
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      {field && filter && (
        <AddFilterConfigure
          field={field}
          initialFilter={filter}
          options={options}
          variant="inline"
          onApply={onApply}
          onBack={onClose}
        />
      )}
    </Popover>
  );
};
